// frontend/src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackAuthLogout, trackAuthTokenValidationLogin } from '../config/analytics';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthRoute = location.pathname === '/login' || location.pathname.startsWith('/auth/');

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${import.meta.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }

    setUser(null);
    
    // Track logout event
    trackAuthLogout();

    if (!isAuthRoute) {
      navigate('/login', {
        replace: true,
      });
    }
  }, [navigate, isAuthRoute]);

  const checkAuth = useCallback(async () => {
    let isAuthenticated = false;
    try {
      const response = await fetch(`${import.meta.env.REACT_APP_API_URL}/auth/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(prevUser => {
          // Track successful authentication check (returning user) only if user wasn't set before
          if (data.data && !prevUser) {
            trackAuthTokenValidationLogin();
          }
          return data.data;
        });
        isAuthenticated = true;
      } else {
        handleLogout();
      }
    } catch (error) {
      handleLogout();
    } finally {
      setLoading(false);
    }
    return isAuthenticated;
  }, [handleLogout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    loading,
    logout: handleLogout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
